/**
 * This file holds functions that performs actual state modifications
 * Please refer to https://vuex.vuejs.org/guide/mutations.html for more information
 * Also mutations MUST BE SYNCHRONOUS https://vuex.vuejs.org/guide/mutations.html#mutations-must-be-synchronous
 */
import * as types from "./types";
import { secondaryImage, checkerboarder, views } from "./initialValues";

const mutations = {
  /**
   * @function SAVE_DIMENSIONS_OF_UPLOADED_IMAGE
   * @param {object} state Current state of the application.
   * @param {object} payload Duple with width and height
   */
  [types.SAVE_DIMENSIONS_OF_UPLOADED_IMAGE](state, payload) {
    state.uploadedImageWidth = payload[0];
    state.uploadedImageHeight = payload[1];
  },
  /**
   * @function SAVE_ADDRESS_OF_UPLOADED_IMAGE
   * @param {object} state Current state of the application.
   * @param {object} payload Response object from the server.
   * @param {string} payload.ugc_image_path For example "https://dev.vans.com/vfdp-customs-img-serv-pre-prod/ugc_images/e44ab21a1cbd2e4accc1d810b0ddcb16.png"
   */
  [types.SAVE_ADDRESS_OF_UPLOADED_IMAGE](state, payload) {
    if (
      typeof payload !== 'object'
      || typeof payload.ugc_image_path !== 'string'
      || payload.ugc_image_path.length === 0
    ) {
      return console.error('Response data must have a `ugc_image_path` key with an absolute URL pointing to the image that was recently uploaded.');
    }

    state.uploadedImageUrl = payload.ugc_image_path;
    state.uploadedImageUrlNoResize = payload.ugc_image_path_noresize ?? payload.ugc_image_path;
    state.uploadedImageId = payload.temp_image_id;
  },
  [types.SAVE_TYPE_OF_UPLOADED_IMAGE](state, data) {
    state.uploadedImageType = data;
  },
  [types.RESET_UPLOADED_IMAGE_DATA](state) {
    state.uploadedImageId = "";
    state.uploadedImageUrl = "";
    state.uploadedImageType = "";
    state.uploadedImageWidth = 0;
    state.uploadedImageHeight = 0;
  },

  [types.UPDATE_SECONDARY_IMAGE](state, data) {
    state.secondaryImage = {
      ...state.secondaryImage,
      ...data,
    };
  },
  [types.RESET_SECONDARY_IMAGE](state) {
    state.secondaryImage = { ...secondaryImage };
  },

  [types.UPDATE_CHECKERBOARDER](state, data) {
    state.checkerboarder = {
      ...state.checkerboarder,
      ...data,
    };
  },
  [types.RESET_CHECKERBOARDER](state) {
    state.checkerboarder = { ...checkerboarder };
  },

  [types.SET_SELECTABLE_MODELS_DATA](state, data) {
    state.selectableStyles = data;
  },
  [types.SET_AVAILABLE_UGC_TOOLS](state, data) {
    state.availableUGCTools = data;
  },
  [types.SET_HEADER_DESC](state, data) {
    state.headerDesc = data;
  },
  [types.SELECTED_STYLE](state, data) {
    state.selectedStyle = data;
  },
  [types.SELECTED_STYLE_NAME](state, data) {
    state.selectedStyleName = data;
  },
  /**
   * @function SET_PDM
   * @param {object} state
   * @param {array} payload Duple
   * @param {array.styleName} payload The style key like 'sk8-hi', 'backpack'
   * @param {array.pdmData} payload The data object from the API
   */
  [types.SET_PDM](state, [styleName, pdmData]) {
    state.pdm[styleName] = pdmData;
  },
  [types.SET_SIZES](state, [styleName, data]) {
    state.sizes[styleName] = data;
  },
  [types.SET_ORDER_STYLES](state, data) {
    state.orderStyles = data;
  },
  [types.SET_ORDER_STYLES_APPAREL](state, data) {
    state.orderStylesApparel = data;
  },
  [types.SET_ACTIVE_VIEW](state, data) {
    state.activeView = data;
  },
  [types.SET_ENABLE_CONTROLS](state, data) {
    state.enableControls = data;
  },
  [types.RESTORE_VIEWS](state) {
    state.views = [ ...views ];
  },
  [types.REMOVE_VIEW](state, data) {
    const index = state.views.indexOf(data);
    if (index > -1) {
      state.views.splice(index, 1);
    }
  },
  [types.PREV_VIEW](state) {
    const currentView = state.activeView;
    const stepNumber  = state.views.indexOf(currentView);
    const prevView = state.views[stepNumber - 1];
    state.activeView = prevView;
  },
  [types.SET_HAS_ACCEPTED_TERMS_AND_CONDITIONS](state, payload) {
    state.hasAcceptedTermsAndConditions = payload;
  },
  [types.SET_CONTACT_DETAILS](state, payload) {
    state.contactDetails = payload;
    state.isContactFormValid = true;
  },
  [types.RESET_CONTACT_DETAILS](state) {
    state.contactDetails = {};
    state.isContactFormValid = false;
  },
  [types.SET_OPT_IN](state, payload) {
    state.optIn = payload;
  },
  [types.SET_ENABLE_EMAIL_GATEWAY](state, enableEmailGateway) {
    state.enableEmailGateway = enableEmailGateway;
  },
  [types.USE_SVG_RENDERER](state, payload ) {
    state.useSvgRenderer = payload;
  },
  [types.ENABLE_SMARTCROP](state, payload ) {
    state.enableSmartcrop = payload;
  },
  [types.SET_FEATURE](state, feature) {
    state.feature = feature;
  },
  [types.SET_IMAGE_CATEGORIES](state, { imageCategories = [] }) {
    state.imageCategories = imageCategories;
  },
  [types.SET_MODEL_RECIPES](state, { modelRecipes = {} }) {
    state.modelRecipes = {};

    if (modelRecipes && Object.keys(modelRecipes).length) {
      state.modelRecipes = { ...modelRecipes };
    }
  },
  [types.ENABLE_AUTO_COLOR_MAPPING](state, payload ) {
    state.enableAutoColorMapping = payload;
  },
  [types.SET_IMAGE_LIBRARY_ID](state, payload ) {
    state.imageLibraryId = payload;
  },
  [types.SET_IMAGE_LIBRARY_CATEGORY](state, payload ) {
    state.imageLibraryCategory = payload;
  },
  [types.SET_COLOR_MAPPING_DATA](state, payload) {
      state.colorMappingData = { ...payload };
  },
  [types.SET_APPAREL_MODELS](state, { apparelModels = [] }) {
    state.apparelModels = apparelModels;
  },
  [types.SET_PRODUCT_TYPE](state, { productType = '' }) {
    state.productType = productType;
  },
  [types.SET_SWITCH_TO_APPAREL](state, { switchToApparel = false }) {
    state.switchToApparel = switchToApparel;
  },
  [types.SET_SWITCH_TO_SHOES](state, { switchToShoes = false }) {
    state.switchToShoes = switchToShoes;
  },
  [types.SET_IMAGE_STATUS](state, payload) {
    state.imageStatus = payload;
  },
  [types.SET_SOURCE](state, { source = '' }) {
    state.source = source;
  },
};

export default mutations;
